import {
    BasicResponsiveImage,
    StandardTile,
    StandardTileContents,
    StandardTileImage,
    StandardTileName,
    StandardTilePrice,
} from '@vp/swan';
import { FormattedReactMessage, __ } from '@99designs/i18n';
import { Price } from '@99designs/pricing';
import { NavigationTracking, sendNavigationEvent } from '@99designs/tracking';
import type { PriceType } from '@99designs/pricing';

interface ProductTileProps {
    title: string;
    imageSrc: string;
    imageAspectRatio: number;
    ctaLink: string;
    trackingData?: NavigationTracking & { crossPlatformTracking?: string };
    price?: PriceType;
    priceLoading?: boolean;
    isTieringProduct?: boolean;
}

const buildHref = (ctaLink: string, crossPlatformTracking?: string) => {
    if (!crossPlatformTracking) {
        return ctaLink;
    }

    const url = new URL(ctaLink, 'https://vistaprint.com');
    const newCtaLink = ctaLink.replace(url.search, '');
    const params = new URLSearchParams(
        crossPlatformTracking.startsWith('?') || crossPlatformTracking.startsWith('&')
            ? crossPlatformTracking.slice(1)
            : crossPlatformTracking
    );

    params.forEach((value, key) => {
        url.searchParams.set(key, value);
    });

    return newCtaLink + url.search;
};

export function ProductTile({
    title,
    imageSrc,
    imageAspectRatio,
    ctaLink,
    trackingData,
    price,
    priceLoading,
    isTieringProduct,
}: ProductTileProps) {
    const href = buildHref(ctaLink, trackingData?.crossPlatformTracking);
    const showFromPrice = isTieringProduct;

    return (
        <StandardTile
            skin="standard"
            imageWidth="full-bleed"
            render={({ className, children }) => (
                <a
                    href={href}
                    onClick={() => {
                        if (trackingData) {
                            const { crossPlatformTracking: _, ...navigationData } = trackingData;
                            sendNavigationEvent(navigationData);
                        }
                    }}
                    data-section={trackingData?.label}
                    data-translation={trackingData?.navigationDetail}
                    data-testid="product-tile"
                    className={className}
                >
                    {children}
                </a>
            )}
        >
            <StandardTileImage>
                <BasicResponsiveImage
                    style={{ height: '100%', objectFit: 'cover' }}
                    aspectRatio={imageAspectRatio}
                    src={imageSrc}
                    alt=""
                    role="presentation"
                />
            </StandardTileImage>
            <StandardTileContents>
                <StandardTileName>{title}</StandardTileName>
                <StandardTilePrice>
                    {showFromPrice ? (
                        <FormattedReactMessage
                            message={__('From <Price/>', {
                                _context: 'Starting price for a design service product',
                            })}
                            replacements={{
                                Price: <Price price={price} loading={priceLoading} />,
                            }}
                        />
                    ) : (
                        <Price price={price} loading={priceLoading} />
                    )}
                </StandardTilePrice>
            </StandardTileContents>
        </StandardTile>
    );
}
